/* @img-migrated */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Volume2, VolumeX, SkipForward, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

import Img from '../components/Img';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_WHITE = '/brand/logo-white.png';

const DEFAULT_SETTINGS = {
  video_url: 'https://www.youtube.com/watch?v=iNO-2YAwgfI',
  youtube_video_id: 'iNO-2YAwgfI',
  title: 'Welcome to the Dominican Chamber of Commerce of Florida',
  subtitle: 'A quick moment to set the tone — then step into your new community.',
  announcement: '',
  autoplay: true,
  muted: true,
  loop: false,
  show_skip: true,
  primary_cta_text: 'Enter the Community',
  secondary_cta_text: 'Skip for now',
  background_type: 'dark',
  show_logo: true,
  logo_position: 'center',
  destination_url: '/app/community',
};

const Welcome = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [muted, setMuted] = useState(true);
  const [completing, setCompleting] = useState(false);
  const playerRef = useRef(null);
  const playerInstanceRef = useRef(null);
  const settingsLoadedRef = useRef(false);

  // Load DB-driven settings once
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/welcome-settings`, { withCredentials: true });
        const merged = { ...DEFAULT_SETTINGS, ...res.data };
        setSettings(merged);
        setMuted(!!merged.muted);
      } catch {
        // keep DEFAULT_SETTINGS — Welcome still works without backend
      } finally {
        settingsLoadedRef.current = true;
      }
    })();
  }, []);

  // Boot the YouTube IFrame API (load once, then init player)
  useEffect(() => {
    if (!settings.youtube_video_id) return undefined;

    let cancelled = false;

    const init = () => {
      if (cancelled) return;
      if (!playerRef.current || !window.YT?.Player) return;
      try { playerInstanceRef.current?.destroy?.(); } catch { /* */ }
      playerInstanceRef.current = new window.YT.Player(playerRef.current, {
        videoId: settings.youtube_video_id,
        playerVars: {
          autoplay: settings.autoplay ? 1 : 0,
          mute: settings.muted ? 1 : 0,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          iv_load_policy: 3,
          fs: 0,
          disablekb: 1,
          playsinline: 1,
          loop: settings.loop ? 1 : 0,
          playlist: settings.loop ? settings.youtube_video_id : undefined,
          showinfo: 0,
        },
        events: {
          onReady: (e) => { try { if (settings.autoplay) e.target.playVideo(); } catch { /* */ } },
          onStateChange: (e) => { if (e.data === 0 && !settings.loop) handleEnter(); },
        },
      });
    };

    if (window.YT && window.YT.Player) { init(); }
    else {
      if (!document.getElementById('yt-iframe-api')) {
        const tag = document.createElement('script');
        tag.id = 'yt-iframe-api';
        tag.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(tag);
      }
      window.onYouTubeIframeAPIReady = init;
    }

    return () => {
      cancelled = true;
      try { playerInstanceRef.current?.destroy?.(); } catch { /* */ }
    };
     
  }, [settings.youtube_video_id, settings.autoplay, settings.muted, settings.loop]);

  const toggleMute = () => {
    try {
      if (!playerInstanceRef.current) return;
      if (muted) {
        playerInstanceRef.current.unMute();
        playerInstanceRef.current.setVolume(80);
      } else {
        playerInstanceRef.current.mute();
      }
      setMuted(!muted);
    } catch { /* */ }
  };

  const markCompleted = async () => {
    try {
      await axios.post(`${API}/auth/onboarding/complete`, {}, { withCredentials: true });
      setUser((prev) => ({ ...prev, is_first_login: false }));
    } catch { /* */ }
  };

  const handleEnter = async () => {
    if (completing) return;
    setCompleting(true);
    await markCompleted();
    navigate(settings.destination_url || '/app/community', { replace: true });
  };

  const handleSkip = async () => {
    if (completing) return;
    setCompleting(true);
    await markCompleted();
    navigate(settings.destination_url || '/app/community', { replace: true });
  };

  const firstName = (user?.display_name || user?.name || '').split(' ')[0];
  const showGradient = settings.background_type === 'gradient';

  const bgClass = showGradient
    ? 'bg-gradient-to-br from-[#001224] via-[#002249] to-[#0b1a2c]'
    : 'bg-[#001224]';

  return (
    <div
      className={`fixed inset-0 z-[60] overflow-hidden text-white ${bgClass}`}
      data-testid="welcome-experience"
    >
      {/* Decorative ribbons */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-[#002249] blur-3xl opacity-70" />
        <div className="absolute -bottom-40 -left-40 w-[420px] h-[420px] rounded-full bg-[#E63946]/20 blur-3xl" />
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'repeating-linear-gradient(115deg, transparent, transparent 90px, rgba(255,255,255,0.04) 90px, rgba(255,255,255,0.04) 91px)',
          }}
        />
      </div>

      <div className="relative z-10 h-full w-full flex flex-col items-center justify-start sm:justify-center px-4 py-8 sm:py-10 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          className={`text-center mb-5 sm:mb-7 ${settings.logo_position === 'top' ? 'self-center mt-2' : ''}`}
        >
          {settings.show_logo && (
            <Img variant="generic"
              src={LOGO_WHITE}
              alt="DCCF"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              className="h-14 sm:h-16 mx-auto mb-3 object-contain"
              data-testid="welcome-logo"
            />
          )}
          <p className="text-[10.5px] sm:text-[11px] uppercase tracking-[0.25em] text-white/60 font-semibold">
            Dominican Chamber of Commerce of Florida
          </p>
          <h1 className="text-2xl sm:text-[32px] font-bold mt-3 tracking-tight leading-tight max-w-[720px] mx-auto" data-testid="welcome-title">
            {firstName && settings.title?.startsWith('Welcome to') ? `Welcome, ${firstName}.` : settings.title}
          </h1>
          {settings.subtitle && (
            <p className="text-sm sm:text-[15px] text-white/70 mt-1.5 max-w-[560px] mx-auto" data-testid="welcome-subtitle">
              {settings.subtitle}
            </p>
          )}
          {settings.announcement && (
            <div className="mt-3 inline-block text-[12.5px] sm:text-[13px] text-white/85 bg-white/8 border border-white/15 px-4 py-2 rounded-full max-w-[640px] backdrop-blur-sm" data-testid="welcome-announcement">
              {settings.announcement}
            </div>
          )}
        </motion.div>

        {/* Video frame */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.15 }}
          className="relative w-full max-w-[880px] aspect-video rounded-2xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.65)] ring-1 ring-white/10 bg-black"
          data-testid="welcome-video-frame"
        >
          <div id="welcome-yt-player" ref={playerRef} className="absolute inset-0 w-full h-full pointer-events-none" />
          <div className="absolute top-0 inset-x-0 h-14 bg-gradient-to-b from-[#001224] via-[#001224]/85 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-[#001224] via-[#001224]/90 to-transparent pointer-events-none" />

          <button
            type="button"
            onClick={toggleMute}
            className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 h-10 px-3 rounded-full bg-white/12 backdrop-blur-md text-white border border-white/20 hover:bg-white/20 transition-colors text-[12.5px] font-medium flex items-center gap-1.5 pointer-events-auto u-btn"
            data-testid="welcome-mute-toggle"
          >
            {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            {muted ? 'Tap for sound' : 'Sound on'}
          </button>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.35 }}
          className="w-full max-w-[880px] mt-6 sm:mt-8 flex flex-col-reverse sm:flex-row items-center justify-center sm:justify-between gap-3 sm:gap-4"
        >
          {settings.show_skip ? (
            <button
              type="button"
              onClick={handleSkip}
              disabled={completing}
              className="text-[13px] sm:text-[14px] text-white/60 hover:text-white/90 transition-colors inline-flex items-center gap-1.5"
              data-testid="welcome-skip-btn"
            >
              <SkipForward className="w-3.5 h-3.5" />
              {settings.secondary_cta_text || 'Skip for now'}
            </button>
          ) : <span />}
          <button
            type="button"
            onClick={handleEnter}
            disabled={completing}
            className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 sm:h-[52px] px-6 sm:px-7 rounded-full bg-[#E63946] hover:bg-[#d02e3b] text-white font-semibold text-[14.5px] sm:text-[15px] shadow-[0_12px_30px_-8px_rgba(230,57,70,0.55)] u-btn"
            data-testid="welcome-enter-btn"
          >
            {settings.primary_cta_text || 'Enter the Community'}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-[11px] text-white/35 mt-6 sm:mt-8 tracking-wide"
        >
          This welcome plays only once — to set the tone, not to slow you down.
        </motion.p>
      </div>
    </div>
  );
};

export default Welcome;
